/* eslint-disable no-unused-vars */
import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, mapTo, tap } from 'rxjs/operators';
import { CreateAccount } from 'src/app/accounts/interfaces/accounts.types';
import { AccountsService } from 'src/app/accounts/services/accounts.service';
import { LaboratoryName } from 'src/app/onboarding/lab/enums/lab.enum';
import {
  OnboardingIntentStep,
  OnboardingType,
} from 'src/app/shared/enums/intent.enum';
import {
  OnboardingPaths,
  UserIntentState,
} from 'src/app/shared/interfaces/intent.interface';
import { environment } from 'src/environments/environment';
import { IntentService } from '../intent.service';
import { ADRIALAB_STEPS, BIOLAB_STEPS } from '../steps';
import { IntentStrategy, IntentStrategyConfig } from './intent.strategy';

export class LabIntentStrategy implements IntentStrategy {
  private intentService: IntentService;
  private router: Router;
  private accountService: AccountsService;

  constructor(private injector: Injector, private labName: LaboratoryName) {
    this.intentService = this.injector.get(IntentService);
    this.router = this.injector.get(Router);
    this.accountService = this.injector.get(AccountsService);
  }

  public getConfig(): IntentStrategyConfig {
    const PREFIX = 'ONBOARDING.LAB.';

    return {
      steps: this.getLabIntentSteps(),
      type: OnboardingType.LAB,
      [OnboardingIntentStep.Welcome]: {
        title: PREFIX + 'WELCOME.WELCOME',
        description: PREFIX + 'WELCOME.TO_VIEW_YOUR_LABORATORY_RESULTS',
      },
      [OnboardingIntentStep.Caution]: {
        paragraph1: 'ACCOUNTS.CAUTION.LABORATORY.PARAGRAPH_1',
        paragraph2: 'ACCOUNTS.CAUTION.LABORATORY.PARAGRAPH_2',
      },
      [OnboardingIntentStep.Complete]: {
        title: PREFIX + 'COMPLETE.YOUR_LABORATORY_RESULTS',
        description: PREFIX + 'COMPLETE.WHEN_YOUR_RESULTS',
      },
    };
  }

  public onWelcomeInit(forceInit: boolean): Observable<any> {
    const { adrialab, biolab } = environment.intents;
    const labName =
      this.labName === LaboratoryName.ADRIA_LAB ? adrialab : biolab;

    if (
      !this.intentService.isUserInIntent(this.intentService.state$.value) ||
      forceInit
    ) {
      return this.intentService.startIntent(labName);
    } else {
      return of({});
    }
  }

  public onAppInit(milestone: OnboardingIntentStep): void {
    this.redirectToMilestone(milestone);
  }

  public onWelcomeLogin(): void {
    this.onNextStep(OnboardingIntentStep.Login).subscribe();
  }

  public onWelcomeCreateAccount(): void {
    this.onNextStep(OnboardingIntentStep.Register).subscribe();
  }

  public onRegisterCreateAccount({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    skipEmailVerification,
    ...rest
  }: CreateAccount): Observable<any> {
    return this.accountService.create(rest).pipe(
      tap(() => {
        this.onNextStep(OnboardingIntentStep.Newsletter).subscribe();
      })
    );
  }

  public onRegisterLogin(): void {
    this.onNextStep(OnboardingIntentStep.Login).subscribe();
  }

  public onNewsletterNextStep(): void {
    this.onNextStep(OnboardingIntentStep.Caution).subscribe();
  }

  public onCautionNextStep(): void {
    this.onNextStep(OnboardingIntentStep.Check).subscribe();
  }

  public onNotValidatedEmail(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Check).pipe(mapTo(false));
  }

  public onVerifyGoToLogin(): void {
    this.onNextStep(OnboardingIntentStep.Login).subscribe();
  }

  public onEmailActivation(): Observable<boolean> {
    if (
      this.intentService.state$?.value?.milestoneState !==
      OnboardingIntentStep.Check
    ) {
      return this.onNextStep(OnboardingIntentStep.Check).pipe(mapTo(false));
    } else {
      return of(true);
    }
  }

  public onSkipEmailActivation(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Onboarding).pipe(mapTo(false));
  }

  public onDownloadAfterCheckSkipClick(): void {
    this.onNextStep(OnboardingIntentStep.End).subscribe();
  }

  public onAlreadyActivitedEmailGoToLogin(): void {
    this.onNextStep(OnboardingIntentStep.Login).subscribe();
  }

  public onLoginRegister(): void {
    this.onNextStep(OnboardingIntentStep.Register).subscribe();
  }

  public onLoginSuccessfully(): void {
    const currentStep = this.intentService.state$.value.milestoneState;

    const stepsToSkip = [
      OnboardingIntentStep.Newsletter,
      OnboardingIntentStep.Caution,
      OnboardingIntentStep.Check,
      OnboardingIntentStep.Login,
      OnboardingIntentStep.Register,
    ];

    if (stepsToSkip.includes(currentStep)) {
      this.onNextStep(OnboardingIntentStep.Onboarding).subscribe();
    } else {
      this.redirectToMilestone(currentStep);
    }
  }

  public onCreateCustomerSuccessfully(): void {
    this.onNextStep(OnboardingIntentStep.Claim).subscribe();
  }

  public onTrackSkipClick(): void {
    this.onNextStep(OnboardingIntentStep.Claim).subscribe();
  }

  public onLabClaimNextClick(): void {
    this.onNextStep(OnboardingIntentStep.Complete).subscribe();
  }

  public onLabClaimCancelIntent(): Observable<unknown> {
    return this.intentService.cancelIntent();
  }

  public onCompleteCloseClick(): void {
    this.onNextStep(OnboardingIntentStep.DownloadAfterCheck).subscribe();
  }

  public onCustomerNotExists(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Onboarding).pipe(mapTo(false));
  }

  public onCustomerExists(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Claim).pipe(mapTo(false));
  }

  public onCustomerWelcomeExists(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Welcome).pipe(map(() => false));
  }

  public onUserNotExists(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Login).pipe(mapTo(false));
  }

  private onNextStep(
    milestone?: OnboardingIntentStep
  ): Observable<UserIntentState | unknown> {
    return this.intentService
      .nextStep(milestone)
      .pipe(
        tap(({ milestoneState }) => this.redirectToMilestone(milestoneState))
      );
  }

  private redirectToMilestone(milestone: OnboardingIntentStep) {
    const { steps } = this.getConfig();
    const { path, state, queryParams } = steps[milestone];

    this.router.navigate([path], { state, queryParams });
  }

  private getLabIntentSteps(): OnboardingPaths {
    switch (this.labName) {
      case LaboratoryName.ADRIA_LAB:
        return ADRIALAB_STEPS;
      case LaboratoryName.BIO_LAB:
        return BIOLAB_STEPS;
    }
  }
}
